import * as React from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import BlogPublishInfo from '../BlogPublishInfo.tsx';
import featureFlagsPng from "../../static/feature_flags_main_image.png";
import ABTestingPng from "../../static/abtesting.png";
import featureRolloutRulesPng from "../../static/rollout_rules.png";
import featureGradualRolloutPng from "../../static/gradual_rollout.png";
import ImageListItem from '@mui/material/ImageListItem';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Alert from '@mui/material/Alert';

export default function FeatureFlagsBlog03() {
    return (
        <Container id="FeatureFlagsBlog03" sx={{ my: { sm: 0, xs: 5 }}} fixed>
          <Box sx={{  minHeight: '50vh' }}>
              <Typography variant="h1" component="h1" align="center">
                Feature Flags and Controlled Rollouts
              </Typography>
              <BlogPublishInfo date="02nd Feb 2025"/>
              <Divider />
              <ImageListItem sx={{ py: { sm: 2, xs: 8 }, px: { sm: 10, xs: 8 }, mx: {sm: '6vw'} }}>
                <img
                    srcSet={featureFlagsPng}
                    alt={"microservices"}
                    loading="lazy"
                  />
              </ImageListItem>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Overview
              </Typography>
              <Typography variant="body1" align="left">
                <b>Feature Flags</b> help us to <b>release new features smoothly, control rollouts, quick rollbacks</b> and helps analyze impact of new features.
                <br/>In this article will cover the common practises and things to keep in mind when using Feature Flags with your backend services.
                This article is purely based on my experience in the software industry and there would be difference of opinions, constructive criticism is always welcome to help us improve.
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                What is a Feature Flag?
              </Typography>
              <Typography variant="body1" align="left">
                A Feature Flag is a <b>switch mechanism</b> that is used when we want to release a new feature to our users in a live application with millions of users and want to do it smoothly.
                We place the Release of Functionality behind the feature flag so that by turning ON/OFF the feature flag we control the rollout without the need for service redeployments that otherwise lead to downtimes.
                <br/>
                Feature Flag has a unique identifier key associated with it to identify it uniquly across the application usage.
                For example a feature that displays a user their invoices can have a unique key as <b>`feature_user_invoices`</b>
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Why Feature Flags?
              </Typography>
              <Typography variant="body1" align="left">
                what compels us to choose feature flags over direct feature release or some hardcoded feature enabling config?
                <List sx={{ listStyleType: 'disc', paddingX: {sm: 2, xs: 2} }}>
                  <ListItem sx={{ display: 'list-item' }}>
                    <b>Smooth Feature Release</b> as we can release/rollaback in a controlled manner without need to redeploy services and this is very helpful for critical releases
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    <b>Controlled Debugging.</b> no need to worry that you might crash the entire app, you can target test users and try out the features yourself
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    <b>Reduced broken functionality time during releases.</b> If we notice that the feature is causing application to crash or to freeze we can turn off feature immediately and then dive deeper to find root cause
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    <b>Speed of feature delivery.</b> resolving merge conflicts when working on a feature in a isolated branch for long time can be a nightmare, with Feature flags we can integrate smaller components of feature in main code easily, specially if the feature will be taking 2-4 months to get ready for initial testing
                  </ListItem>
                </List>
                <Alert variant="filled" severity="warning">
                  Please evaluate the individual needs of your product before choosing feature flags. 
                  <br/>
                  When to use Feature Flags?
                  <br/>
                  your product is around for a while and has a large userbase then Feature Flags is the way ahead.<br/>
                  there are multiple teams working on the same interdependent features this also makes a nice case for using feature flags
                </Alert>
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                How to use feature flags ?
              </Typography>
              <Typography variant="body1" align="left">
                Integration of Feature Flags can happen in two ways:
                <List sx={{ listStyleType: 'disc', paddingX: {sm: 2, xs: 2} }}>
                  <ListItem sx={{ display: 'list-item' }}>
                    <b>[Ideal Way] Server Side Feature Flags: Integrate feature flags with you microservices</b>
                    <ImageListItem sx={{ py: { sm: 2, xs: 8 }, px: { sm: 10, xs: 8 }, mx: {sm: '10vw'} }}>
                      <img
                          srcSet={featureFlagsPng}
                          alt={"featureflagcheck"}
                          loading="lazy"
                        />
                    </ImageListItem>
                    <br/>
                    Let us say we have a system that will return user invoices based on whether the feature flag is active/inactive
                    <List sx={{ listStyleType: 'disc', paddingX: {sm: 2, xs: 2} }}>
                      <ListItem sx={{ display: 'list-item' }}>
                        We create a feature flag with unique key, lets say `feature_user_invoices` and turn it ON via our simple CRUD page for features(we can build this internally within organization or use third party providers)
                      </ListItem>
                      <ListItem sx={{ display: 'list-item' }}>
                        Within your API Endpoint, call your Feature Flag Service(build a service that exposes a endpoint to check if feature is active or not), something like 
                        `/experimentation/checkFeatureStatus?key="feature_user_invoices"`
                      </ListItem>
                      <ListItem sx={{ display: 'list-item' }}>
                        Based on response from experimentation service decide to respond with list of invoices or not, if feature turned off we can return [] response of invoices or return a suitable 4xx code to indicate in Frontend that user does not have access yet to the feature
                      </ListItem>
                    </List>
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    [Other Way] Client Side Feature Flags: Authenticated user will be sent all features they can access on login
                    <br/>
                    <List sx={{ listStyleType: 'disc', paddingX: {sm: 2, xs: 2} }}>
                      <ListItem sx={{ display: 'list-item' }}>
                        on successful login we send user a list of active features like feature: [feature_user_invoices]
                      </ListItem>
                      <ListItem sx={{ display: 'list-item' }}>
                        If the feature is incase then on invoives page we will show hardcoded message and not make the api call to get invoices.
                      </ListItem>
                    </List>
                  </ListItem>
                    <Alert variant="filled" severity="warning">
                      This other approach comes with apparent flaw that the state is maintained until user relogin, this can be optimised to fetch active features every 5 minutes in user session or during page reloads.
                      avoid this approach whenever possible, backend based approach works efficiently
                  </Alert>
                  </List>
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                AB Testing and Experimentation with feature Flags
              </Typography>
              <Typography variant="body1" align="left">
                What is AB Testing?
                <br/>
                A/B testing software helps you compare different versions(With/Without feature flag(s)) of a website or app to see which performs better.
                <br/>
                <br/>
                <Alert variant="filled" severity="warning">
                  AB Testing is the second last step before we decide to do a full rollout of feature or rollback or scrap the feature. 
                </Alert>
                <br/>
                standard practise to keep monitoring both logs/alerts during the start AB Testing Phase
                <br/>
                <br/>
                We have <b>Target Group</b> that gets the feature and a <b>Control group</b> that does not get the new feature as follows:
                What decides the allocation into this groups? we can use random distribution algorithm to assign users to this groups or build a complex algorithm to allocate based on past user behaviours(usually requiring inputs from Data Analysts)
                <ImageListItem sx={{ py: { sm: 2, xs: 8 }, px: { sm: 10, xs: 8 }, mx: {sm: '10vw'} }}>
                  <img
                      srcSet={ABTestingPng}
                      alt={"goodmicroservices"}
                      loading="lazy"
                    />
                </ImageListItem>
                <br/>
                We usually run a AB test for a span of 3-4 weeks when trying to gauge GMV impact or 2-4 days when trying to just release features with caution. We usually setup in the config of the AB Test when we want to start and end the test
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Feature Flag Rules/Filters
              </Typography>
              <Typography variant="body1" align="left">
                what if we want to target users based on certain criteria to see the new feature?
                <br/>
                We can add rules in Feature Flag config as needed as follows:
                <ImageListItem sx={{ py: { sm: 2, xs: 8 }, px: { sm: 10, xs: 8 }, mx: {sm: '10vw'} }}>
                  <img
                      srcSet={featureRolloutRulesPng}
                      alt={"featureflagrules"}
                      loading="lazy"
                    />
                </ImageListItem>
                <br/>
                <br/>
                Rules can usually include the following but not limited to:
                <List sx={{ listStyleType: 'disc', paddingX: {sm: 2, xs: 2} }}>
                  <ListItem sx={{ display: 'list-item' }}>
                    app_type
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    app_version
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    user_id(s)
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    country
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    city_id
                  </ListItem>
                </List>
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Gradual Rollouts in Live Environment
              </Typography>
              <Typography variant="body1" align="left">
              <ImageListItem sx={{ py: { sm: 2, xs: 8 }, px: { sm: 10, xs: 8 }, mx: {sm: '10vw'} }}>
                  <img
                      srcSet={featureGradualRolloutPng}
                      alt={"circuitbreaker"}
                      loading="lazy"
                    />
                </ImageListItem>
                <br/>
                When doing controlled feature rollout it is usually good to follow 20/80 rule.
                <br/>
                We can rollout at 20% and keep observing our monitoring alerts/logs/dashboards for a few hours and then if nothing seems to be broken then we can rollout to a larger audience
                If the functionality is touching critical flows then we can start at 5% and observe for 4-5 hours and then increase rollout percentage.
                <br/>
                <br/>
                Incase we are monitoring <b>GMV Impact</b> of the new feature then usually that happens over the course of 3-4 weeks atleast wherein we have control groups and target groups in that case aswell we can do a controlled rollout to target a smaller percentage first and then target a bigger group
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Managing your Feature Flags
              </Typography>
              <Typography variant="body1" align="left">
                  as we integrate feature flags we can end up with 100s of feature flags in our system. how do we manage them?
                  <br/>
                  We can build a internal tool to keep CRUD page of feature flags and AB Testing Experiments or use third party providers of experimentation platform, wherein we can turn ON/OFF features and start/end AB Test experiments as per our requirements
                  <br/>
                  <br/>
                  <b>After the feature flag serves its purpose we can discard the same by first removing usage from code and then marking the feature flag itself as inactive. 
                  as the feature flag check adds a overhead on the API response time after the purpose is served we should usually deprecate the feature flags</b>
              </Typography>
          </Box>
        </Container>
    )
};